// Applications dashboard: /pam/applications/dashboard — the home of the
// Applications product (mode mfa_apps). The look is the PAM dashboard's
// (SummaryCard tiles, the access activity chart); the data comes from the
// existing APIs, each section loading and failing on its own:
//
//   apps         POST /api/v1/idp/apps/list, then apps/assignments per app
//                (MFA API apps: their app policies instead, one
//                /api/v1/policyService/ListPolicy call for all of them)
//   sign-in src  POST /api/v1/idp/loginSources/list
//   sign-ins     POST /ad/GetAuthLog — there is no "apps" mode, so per
//                protocol (oidc, saml, mfa_api) two calls: every terminal row
//                (excludeIntermediate, pageSize 100, bucketMs → total, latest
//                rows, timeline) and decision "deny" (pageSize 5 → denied
//                total, latest denials, denied timeline). Six calls per load.
//                The identity provider writes only allow / deny rows
//                (internal/idp/audit.go), so allowed = all − denied.
//
// Plain axios for GetAuthLog, not dispatchGetAuthLog: that helper shares one
// AbortController, so parallel calls would cancel each other.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Select } from "antd";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Activity, AlertTriangle, AppWindow, ArrowRight, Clock, LogIn, Plus, RefreshCw, UsersRound } from "lucide-react";

import { logOutUser } from "../../common";
import { AUTH_TOKEN, DOMAIN_ID, ORG_ID, REACT_APP_API } from "../../constants";
import { PageHeader, PageShell } from "../../design-system/tableUi";
import { AN_COLORS, AN_MONO } from "../../design-system/tokens";
import { idpFailure, listAppAssignments, listApps, listLoginSources } from "../../service/idpService";
import { appPolicyFields, listAppPolicies, normalizePrincipal } from "../../service/appPolicyService";
import { card } from "../enduser/ssh-proxy/sshProxyUi";
import { SummaryCard, fmtUpdated, num } from "../enduser/pam-dashboard/PamDashboard";
import ActivityChart, { OUTCOME_COLORS } from "../enduser/ssh-access-log/ActivityChart";
import { AppLogo, Forbidden, ProtocolBadge, appTypeKey } from "./appsUi";
import { mfaApiReasonLabel } from "./appReasons";

const MIN = 60 * 1000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

// Window presets; each bucket gives a readable bar count (24 and 28 bars).
const WINDOWS = [
  { value: "24h", label: "Last 24 hours", short: "24h", ms: DAY, bucketMs: HOUR },
  { value: "7d", label: "Last 7 days", short: "7d", ms: 7 * DAY, bucketMs: 6 * HOUR },
];

const PROTOCOLS = ["oidc", "saml", "mfa_api"];
const SAMPLE = 100; // GetAuthLog's largest page
const LIST_N = 5;

const CHART_SERIES = [
  { key: "allowed", label: "Allowed", color: OUTCOME_COLORS.approved, of: (b) => b.allowed || 0 },
  { key: "denied", label: "Denied", color: OUTCOME_COLORS.denied, of: (b) => b.denied || 0 },
];

// The identity provider's reason codes (internal/idp: server.go, mfaapi.go).
const REASONS = {
  "fed+mfa": "Signed in with MFA",
  fed: "Signed in",
  push_approved: "Push approved",
  push_denied: "Push declined",
  mfa_denied: "Push declined",
  push_expired: "No answer to the push",
  mfa_expired: "No answer to the push",
  not_assigned: "Not assigned to this app",
  not_in_directory: "Not in the directory",
  mfa_no_device: "No device enrolled",
  mfa_push_disabled: "Push is turned off",
  mfa_rate_limited: "Too many attempts",
  mfa_no_provider: "No MFA provider",
  totp_valid: "Code accepted",
  backup_valid: "Backup code accepted",
  totp_invalid: "Wrong code",
  totp_not_enrolled: "No authenticator set up",
  totp_rate_limited: "Too many wrong codes",
};
// MFA API sign-ins use the policy reason codes (appReasons.js), shared with
// the Sign-in logs.
const reasonText = (r, protocol) => {
  if (!r) return "—";
  if (protocol === "mfa_api" && mfaApiReasonLabel(r)) return mfaApiReasonLabel(r);
  if (REASONS[r]) return REASONS[r];
  const s = String(r).replace(/[_+]/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const SOURCE_KIND = { entra: "Entra ID", ad: "Active Directory" };

const fmtTime = (t) =>
  t ? new Date(t).toLocaleString("en-US", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }) : "—";

// Bucket starts covering the window, aligned like the server's
// (ts / bucketMs) * bucketMs, the last one holding now.
const windowBounds = (win, now) => {
  const b = win.bucketMs;
  const n = Math.round(win.ms / b);
  const last = Math.floor(now / b) * b;
  const starts = Array.from({ length: n }, (_, i) => last - (n - 1 - i) * b);
  return { since: starts[0], until: now, starts };
};

const statusOf = (err) => err?.response?.status ?? err?.status ?? 0;

const authLog = (body) =>
  axios
    .post(
      `${REACT_APP_API}/ad/GetAuthLog`,
      { orgId: ORG_ID(), tenantId: DOMAIN_ID(), ...body },
      { withCredentials: true, headers: { "X-Authorization": AUTH_TOKEN() } },
    )
    .then(({ data }) => {
      if (data && typeof data === "object" && (data.code === 200 || data.status === "Success")) return data;
      // GetAuthLog answers its own failures as 200 { code, message }.
      const e = new Error(typeof data === "object" && data?.message ? data.message : "The server returned an unexpected response.");
      e.status = typeof data === "object" ? data?.code : 0;
      throw e;
    });

const logsErrorText = (err) => {
  const status = statusOf(err);
  if (status === 401) return "Your session has expired. Sign in again.";
  if (status === 403) return "Administrators only.";
  return err?.response?.data?.error || (err?.message && !err?.response ? err.message : "") || "Couldn't read the sign-ins.";
};

const idpErrorState = (err, fallback) => {
  const f = idpFailure(err, fallback);
  if (f.logout) logOutUser();
  return { forbidden: f.forbidden, error: f.forbidden ? "Administrators only." : f.text };
};

/* -------------------------------------------------------------- pieces */

const eyebrowStyle = { fontFamily: AN_MONO, fontSize: 10.5, letterSpacing: 1.1, textTransform: "uppercase", color: AN_COLORS.muted };

// A tile whose data failed to load: SummaryCard's header, the error, Retry.
function ErrorTile({ icon: Icon, label, error, onRetry }) {
  return (
    <div style={{ ...card, padding: "16px 18px", display: "flex", flexDirection: "column", gap: 10 }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
        <span
          style={{
            width: 26,
            height: 26,
            borderRadius: 7,
            background: AN_COLORS.primaryL,
            color: AN_COLORS.primary,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon size={14} />
        </span>
        <span style={{ ...eyebrowStyle, letterSpacing: 1 }}>{label}</span>
      </span>
      <InlineError error={error} onRetry={onRetry} />
    </div>
  );
}

function InlineError({ error, onRetry }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
      <span style={{ display: "inline-flex", alignItems: "flex-start", gap: 6, fontSize: 12.5, color: AN_COLORS.danger, minWidth: 0 }}>
        <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 2 }} />
        {error}
      </span>
      {onRetry && (
        <Button size="small" icon={<RefreshCw size={12} />} onClick={onRetry}>
          Retry
        </Button>
      )}
    </div>
  );
}

function ListCard({ title, to, linkLabel = "View all", children, footer }) {
  return (
    <div style={{ ...card, padding: 16, display: "flex", flexDirection: "column", minWidth: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <span style={eyebrowStyle}>{title}</span>
        {to && (
          <Link to={to} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: AN_COLORS.primary }}>
            {linkLabel}
            <ArrowRight size={12} />
          </Link>
        )}
      </div>
      <div style={{ flex: 1 }}>{children}</div>
      {footer && <div style={{ marginTop: 10, fontSize: 11.5, color: AN_COLORS.muted }}>{footer}</div>}
    </div>
  );
}

const Muted = ({ children }) => (
  <div style={{ minHeight: "6rem", display: "flex", alignItems: "center", justifyContent: "center", color: AN_COLORS.muted, fontSize: 13, textAlign: "center" }}>
    {children}
  </div>
);

const ellipsis = { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

function TopApps({ rows }) {
  const max = Math.max(1, ...rows.map((r) => r.total));
  return (
    <div style={{ display: "grid", gap: 12 }}>
      {rows.map((r) => {
        const name = (
          <span style={{ ...ellipsis, fontWeight: 500, fontSize: 13, color: AN_COLORS.ink }} title={r.name}>
            {r.name}
          </span>
        );
        return (
          <div key={r.key} style={{ display: "grid", gap: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <AppLogo app={r.app} size={24} />
              {r.app ? (
                <Link to={`/pam/applications/${r.app.id}`} style={{ display: "flex", minWidth: 0 }}>
                  {name}
                </Link>
              ) : (
                name
              )}
              {r.protocol && <ProtocolBadge protocol={r.protocol} />}
              <span style={{ marginLeft: "auto", fontWeight: 600, fontSize: 13, color: AN_COLORS.ink2, fontVariantNumeric: "tabular-nums" }}>
                {num(r.total)}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, paddingLeft: "2.125rem" }}>
              <div style={{ flex: 1, height: 6, borderRadius: 3, background: AN_COLORS.line, overflow: "hidden" }}>
                <div style={{ width: `${(r.total / max) * 100}%`, height: "100%", display: "flex", gap: 2 }}>
                  {r.allowed > 0 && <div style={{ flexGrow: r.allowed, flexBasis: 0, background: OUTCOME_COLORS.approved }} />}
                  {r.denied > 0 && <div style={{ flexGrow: r.denied, flexBasis: 0, background: OUTCOME_COLORS.denied }} />}
                </div>
              </div>
              <span style={{ fontSize: 11.5, color: AN_COLORS.muted, whiteSpace: "nowrap" }}>
                {num(r.allowed)} allowed · {num(r.denied)} denied
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RecentDenials({ rows }) {
  return (
    <div style={{ display: "grid" }}>
      {rows.map((e, i) => (
        <div
          key={`${e.correlationId || "x"}-${e.tsUnixMs}-${i}`}
          style={{ display: "grid", gap: 3, padding: "8px 0", borderTop: i ? `1px solid ${AN_COLORS.line}` : "none", minWidth: 0 }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: OUTCOME_COLORS.denied, flexShrink: 0 }} />
            <span style={{ ...ellipsis, fontWeight: 500, fontSize: 13, color: AN_COLORS.ink }} title={e.principal}>
              {e.principal || "Unknown user"}
            </span>
            <span style={{ marginLeft: "auto", fontFamily: AN_MONO, fontSize: 11, color: AN_COLORS.muted, whiteSpace: "nowrap" }}>
              {fmtTime(e.tsUnixMs)}
            </span>
          </div>
          <div style={{ display: "flex", gap: 6, fontSize: 12, color: AN_COLORS.ink3, minWidth: 0, paddingLeft: "1rem" }}>
            <span style={ellipsis} title={e.realm || e.serviceSpn}>
              {e.realm || e.serviceSpn || "Unknown app"}
            </span>
            <span style={{ color: AN_COLORS.muted }}>·</span>
            <span style={{ ...ellipsis, color: AN_COLORS.danger }} title={e.reason}>
              {reasonText(e.reason, e.protocol)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

/* --------------------------------------------------------------- page */

export default function ApplicationsDashboard() {
  const navigate = useNavigate();
  const [winKey, setWinKey] = useState("24h");
  const win = WINDOWS.find((w) => w.value === winKey) || WINDOWS[0];

  // Each section: { loading, error, data }.
  const [apps, setApps] = useState({ loading: true, error: "", forbidden: false, list: null });
  const [assign, setAssign] = useState({ loading: false, error: "", byApp: null, failed: 0 });
  const [sources, setSources] = useState({ loading: true, error: "", list: null });
  const [logs, setLogs] = useState({ loading: true, error: "", data: null });
  const [updatedAt, setUpdatedAt] = useState(null);

  // Drops answers that a newer load (or leaving the page) made stale.
  const seq = useRef({ apps: 0, assign: 0, sources: 0, logs: 0 });
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const ticket = (k) => {
    seq.current[k] += 1;
    const n = seq.current[k];
    return () => alive.current && seq.current[k] === n;
  };

  const loadAssignments = useCallback((list) => {
    const current = ticket("assign");
    if (!list.length) {
      setAssign({ loading: false, error: "", byApp: {}, failed: 0 });
      return Promise.resolve();
    }
    setAssign((s) => ({ ...s, loading: true, error: "" }));
    // MFA API apps have no assignments: their policies say who may use them,
    // all read with one ListPolicy call. Each policy names one directory user.
    const hasMfaApi = list.some((a) => a.protocol === "mfa_api");
    const policies = hasMfaApi ? listAppPolicies() : Promise.resolve([]);
    const calls = list.map((a) =>
      a.protocol === "mfa_api"
        ? policies.then((ps) => ({
            assignments: ps
              .map(appPolicyFields)
              .filter((p) => p.appId === a.id)
              .map((p) => ({ type: "user", principal: `policy:${p.directoryId}:${normalizePrincipal(p.user)}` })),
          }))
        : listAppAssignments(a.id),
    );
    return Promise.allSettled(calls).then((results) => {
      if (!current()) return;
      const byApp = {};
      let failed = 0;
      let lastErr = null;
      results.forEach((r, i) => {
        if (r.status === "fulfilled") byApp[list[i].id] = r.value?.assignments || [];
        else {
          failed += 1;
          // A failed policy read rejects with { status, text }, not an axios error.
          lastErr = r.reason?.text && !r.reason?.response ? Object.assign(new Error(r.reason.text), { status: r.reason.status }) : r.reason;
        }
      });
      if (failed === list.length) {
        setAssign({ loading: false, error: idpErrorState(lastErr, "Couldn't read who is assigned.").error, byApp: null, failed });
      } else {
        setAssign({ loading: false, error: "", byApp, failed });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadApps = useCallback(() => {
    const current = ticket("apps");
    setApps((s) => ({ ...s, loading: true, error: "", forbidden: false }));
    return listApps()
      .then((res) => {
        if (!current()) return;
        const list = res?.apps || [];
        setApps({ loading: false, error: "", forbidden: false, list });
        setUpdatedAt(Date.now());
        return loadAssignments(list);
      })
      .catch((err) => {
        if (!current()) return;
        setApps({ loading: false, list: null, ...idpErrorState(err, "Couldn't read the applications.") });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadAssignments]);

  const loadSources = useCallback(() => {
    const current = ticket("sources");
    setSources((s) => ({ ...s, loading: true, error: "" }));
    return listLoginSources()
      .then((res) => current() && setSources({ loading: false, error: "", list: res?.loginSources || [] }))
      .catch((err) => current() && setSources({ loading: false, error: idpErrorState(err, "Couldn't read the sign-in source.").error, list: null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLogs = useCallback((w) => {
    const current = ticket("logs");
    setLogs((s) => ({ ...s, loading: true, error: "" }));
    const { since, until, starts } = windowBounds(w, Date.now());
    const base = { sinceUnixMs: since, untilUnixMs: until, excludeIntermediate: true, page: 1, bucketMs: w.bucketMs };
    const calls = PROTOCOLS.flatMap((protocol) => [
      authLog({ ...base, protocol, pageSize: SAMPLE }),
      authLog({ ...base, protocol, decision: "deny", pageSize: LIST_N }),
    ]);
    return Promise.all(calls)
      .then((res) => {
        if (!current()) return;
        const allMap = new Map();
        const denyMap = new Map();
        const add = (map, timeline) =>
          (timeline || []).forEach((b) => map.set(Number(b.tsUnixMs), (map.get(Number(b.tsUnixMs)) || 0) + (Number(b.count) || 0)));
        let total = 0;
        let denied = 0;
        let partial = false;
        const sample = [];
        const denials = [];
        PROTOCOLS.forEach((protocol, i) => {
          const all = res[2 * i];
          const deny = res[2 * i + 1];
          const events = all?.events || [];
          total += Number(all?.total) || 0;
          denied += Number(deny?.total) || 0;
          if ((Number(all?.total) || 0) > events.length) partial = true;
          sample.push(...events);
          denials.push(...(deny?.events || []));
          add(allMap, all?.timeline);
          add(denyMap, deny?.timeline);
        });
        const timeline = starts.map((ts) => {
          const d = denyMap.get(ts) || 0;
          return { tsUnixMs: ts, allowed: Math.max(0, (allMap.get(ts) || 0) - d), denied: d };
        });
        denials.sort((a, b) => (b.tsUnixMs || 0) - (a.tsUnixMs || 0));
        setLogs({
          loading: false,
          error: "",
          data: { total, denied, allowed: Math.max(0, total - denied), timeline, sample, partial, denials: denials.slice(0, LIST_N) },
        });
        setUpdatedAt(Date.now());
      })
      .catch((err) => {
        if (!current()) return;
        if (statusOf(err) === 401) logOutUser();
        setLogs({ loading: false, error: logsErrorText(err), data: null });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadApps();
    loadSources();
  }, [loadApps, loadSources]);

  useEffect(() => {
    loadLogs(win);
  }, [win, loadLogs]);

  const refresh = () => {
    loadApps();
    loadSources();
    loadLogs(win);
  };
  const refreshing = apps.loading || sources.loading || logs.loading || assign.loading;

  /* ---------------------------------------------------------- derived */

  const list = apps.list;
  const appStats = useMemo(() => {
    if (!list) return null;
    const by = { saml: 0, web: 0, spa: 0, mfa_api: 0 };
    list.forEach((a) => {
      by[appTypeKey(a)] += 1;
    });
    const enabled = list.filter((a) => a.enabled).length;
    return { total: list.length, enabled, disabled: list.length - enabled, by };
  }, [list]);

  const access = useMemo(() => {
    if (!list || !assign.byApp) return null;
    const users = new Set();
    const groups = new Set();
    // MFA API apps have policies, not assignments: counted apart.
    let unassigned = 0;
    let noPolicy = 0;
    list.forEach((a) => {
      const rows = assign.byApp[a.id];
      if (!rows) return; // could not be read
      if (!rows.length) {
        if (a.protocol === "mfa_api") noPolicy += 1;
        else unassigned += 1;
      }
      rows.forEach((r) => (r.type === "group" ? groups : users).add(String(r.principal)));
    });
    return { users: users.size, groups: groups.size, unassigned, noPolicy };
  }, [list, assign.byApp]);

  const topApps = useMemo(() => {
    const sample = logs.data?.sample;
    if (!sample) return [];
    const byKey = new Map();
    sample.forEach((e) => {
      const key = e.gatewayId || e.realm || e.serviceSpn || "unknown";
      const r = byKey.get(key) || { key, name: e.realm || e.serviceSpn || "Unknown app", protocol: e.protocol, total: 0, allowed: 0, denied: 0 };
      r.total += 1;
      if (e.decision === "deny") r.denied += 1;
      else if (e.decision === "allow") r.allowed += 1;
      byKey.set(key, r);
    });
    return [...byKey.values()]
      .map((r) => {
        // audit.go writes gateway_id "idp:<appId>"; the current name wins.
        const app = (list || []).find((a) => `idp:${a.id}` === r.key);
        return app ? { ...r, app, name: app.name } : r;
      })
      .sort((a, b) => b.total - a.total)
      .slice(0, LIST_N);
  }, [logs.data, list]);

  const needsSource = (list || []).some((a) => a.protocol === "oidc" || a.protocol === "saml");

  /* ------------------------------------------------------------ tiles */

  const appsTile = apps.error ? (
    <ErrorTile icon={AppWindow} label="Applications" error={apps.error} onRetry={loadApps} />
  ) : (
    <SummaryCard
      to="/pam/applications"
      icon={AppWindow}
      label="Applications"
      value={appStats?.total}
      lines={[
        ["SAML", appStats?.by.saml],
        ["Web App", appStats?.by.web],
        ["Single-page", appStats?.by.spa],
        ["MFA API", appStats?.by.mfa_api],
      ]}
      hint={appStats ? `${num(appStats.enabled)} enabled${appStats.disabled ? ` · ${num(appStats.disabled)} disabled` : ""}` : null}
    />
  );

  const signInsTile = logs.error ? (
    <ErrorTile icon={Activity} label={`Sign-ins · ${win.short}`} error={logs.error} onRetry={() => loadLogs(win)} />
  ) : (
    <SummaryCard
      to="/pam/auth-logs"
      icon={Activity}
      label={`Sign-ins · ${win.short}`}
      value={logs.data?.total}
      lines={[
        ["Allowed", logs.data?.allowed, OUTCOME_COLORS.approved],
        ["Denied", logs.data?.denied, OUTCOME_COLORS.denied],
      ]}
    />
  );

  const accessTile = assign.error ? (
    <ErrorTile icon={UsersRound} label="Users with access" error={assign.error} onRetry={() => list && loadAssignments(list)} />
  ) : (
    <SummaryCard
      to="/pam/applications"
      icon={UsersRound}
      label="Users with access"
      value={access ? access.users + access.groups : null}
      lines={[
        ["Users", access?.users],
        ["Groups", access?.groups],
      ]}
      warn={
        access?.unassigned > 0 || access?.noPolicy > 0
          ? [
              access.unassigned > 0 && `${num(access.unassigned)} ${access.unassigned === 1 ? "app has" : "apps have"} no one assigned`,
              access.noPolicy > 0 && `${num(access.noPolicy)} ${access.noPolicy === 1 ? "app has" : "apps have"} no policy`,
            ]
              .filter(Boolean)
              .join(" · ")
          : assign.failed > 0
            ? `Couldn't read ${num(assign.failed)} ${assign.failed === 1 ? "app's" : "apps'"} assignments`
            : null
      }
      hint="Members of an assigned group may sign in too. For MFA API apps, the users their policies name."
    />
  );

  const enabledSources = (sources.list || []).filter((s) => s.enabled);
  const firstSource = enabledSources[0] || (sources.list || [])[0];
  const sourceTile = sources.error ? (
    <ErrorTile icon={LogIn} label="Sign-in source" error={sources.error} onRetry={loadSources} />
  ) : (
    <SummaryCard
      to="/pam/applications/signInSource"
      icon={LogIn}
      label="Sign-in source"
      value={!sources.list ? null : enabledSources.length ? firstSource.identitySourceName || "Directory" : firstSource ? "Disabled" : "Not set"}
      accent={sources.list && !enabledSources.length ? AN_COLORS.warn : AN_COLORS.primary}
      lines={
        firstSource
          ? [
              ["Directory", SOURCE_KIND[firstSource.kind] || firstSource.kind || "—"],
              ["Status", firstSource.enabled ? "Enabled" : "Disabled"],
              ...(enabledSources.length > 1 ? [["Also enabled", `${enabledSources.length - 1} more`]] : []),
            ]
          : []
      }
      warn={
        sources.list && !enabledSources.length
          ? `${firstSource ? "Turned off" : "Not set"}: users can't sign in to OIDC or SAML apps`
          : null
      }
      hint={
        sources.list && !enabledSources.length && !needsSource && list?.length
          ? "Your apps are all MFA API apps, which keep their own login and don't need one."
          : "MFA API apps keep their own login and don't need one."
      }
    />
  );

  /* ------------------------------------------------------------- body */

  const noApps = !apps.error && list && list.length === 0;
  const tileGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 13.75rem), 1fr))",
    gap: 14,
    marginBottom: 14,
  };

  let body;
  if (apps.forbidden) {
    body = <Forbidden />;
  } else if (noApps) {
    body = (
      <>
        <div style={{ ...card, padding: "2rem 1.25rem", display: "grid", gap: 12, justifyItems: "center", textAlign: "center", marginBottom: 14 }}>
          <span
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: AN_COLORS.primaryL,
              color: AN_COLORS.primary,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AppWindow size={20} />
          </span>
          <div style={{ fontSize: 16, fontWeight: 600, color: AN_COLORS.ink }}>Add your first application</div>
          <div style={{ fontSize: 13, color: AN_COLORS.muted, maxWidth: "30rem", lineHeight: 1.5 }}>
            Connect the apps your users sign in to (SAML or OpenID Connect), or give your own app AuthNull's push with the MFA API.
            Sign-ins and who has access show up here.
          </div>
          <Button type="primary" icon={<Plus size={13} strokeWidth={2.2} />} onClick={() => navigate("/pam/applications/new")}>
            Add application
          </Button>
        </div>
        <div style={tileGrid}>{sourceTile}</div>
      </>
    );
  } else {
    body = (
      <>
        <div style={tileGrid}>
          {appsTile}
          {signInsTile}
          {accessTile}
          {sourceTile}
        </div>

        {logs.error ? (
          <div style={{ ...card, padding: 16, marginBottom: 14 }}>
            <div style={{ ...eyebrowStyle, marginBottom: 12 }}>Sign-in activity</div>
            <InlineError error={logs.error} onRetry={() => loadLogs(win)} />
          </div>
        ) : (
          <ActivityChart
            timeline={logs.data?.timeline || []}
            bucketMs={win.bucketMs}
            loading={logs.loading}
            title="Sign-in activity"
            emptyText="No sign-ins in this window."
            series={CHART_SERIES}
          />
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 22rem), 1fr))", gap: 14 }}>
          <ListCard
            title="Top applications"
            to="/pam/applications"
            footer={logs.data?.partial && topApps.length ? `From the latest ${num(logs.data.sample.length)} sign-ins.` : null}
          >
            {logs.error ? (
              <InlineError error={logs.error} onRetry={() => loadLogs(win)} />
            ) : topApps.length ? (
              <TopApps rows={topApps} />
            ) : (
              <Muted>{logs.loading ? "Loading…" : "No sign-ins in this window."}</Muted>
            )}
          </ListCard>
          <ListCard title="Recent denials" to="/pam/auth-logs">
            {logs.error ? (
              <InlineError error={logs.error} onRetry={() => loadLogs(win)} />
            ) : logs.data?.denials?.length ? (
              <RecentDenials rows={logs.data.denials} />
            ) : (
              <Muted>{logs.loading ? "Loading…" : "No denied sign-ins in this window."}</Muted>
            )}
          </ListCard>
        </div>
      </>
    );
  }

  return (
    <PageShell>
      <PageHeader
        eyebrow="Applications · Overview"
        title="Dashboard"
        subtitle={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Clock size={12} />
            Updated {fmtUpdated(updatedAt)}
          </span>
        }
        actions={
          !apps.forbidden && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {!noApps && (
                <Select
                  value={winKey}
                  onChange={setWinKey}
                  options={WINDOWS.map(({ value, label }) => ({ value, label }))}
                  style={{ width: "9.5rem" }}
                  aria-label="Time window"
                />
              )}
              <Button icon={<RefreshCw size={14} />} onClick={refresh} loading={refreshing}>
                Refresh
              </Button>
            </div>
          )
        }
      />
      {body}
    </PageShell>
  );
}
